import { PRODUTO_PADRAO } from '@/data/config';
import { request } from '@/data/http';
import type { CompilacoesDataSource } from '@/data/types';
import {
  toBuildDetalhe,
  toCapabilitiesDoUsuario,
  toEventoCapability,
  toEventoVisibilidade,
  toVisibilidade,
  toVisibilidadeFuncionalidade,
  toVisibilidadeRamo,
  toIncidenteDetalhe,
  toIncidenteResumo,
  toMe,
  toPaginaIncidentes,
  toPagina,
  toPainel,
  toRamo,
  toResumo,
  toUsuarioComCapabilities,
  toUsuarioKeycloak,
} from './normalize';

/**
 * Fonte REST — fala com a API do Compilações conforme `docs/CONTRATO.md`.
 * Cada método é uma rota do contrato, sem regra de negócio: a API decide,
 * esta camada só tipa (via `normalize`) e nomeia o recurso para a mensagem
 * de erro.
 */

/** A versão vai no path; `encodeURIComponent` protege de branch exótico. */
function caminhoVersao(versao: string): string {
  return `/v1/builds/${encodeURIComponent(versao)}`;
}

function caminhoIncidente(id: number): string {
  return `/v1/incidentes/${id}`;
}

const ADMIN = '/v1/admin/capabilities';
const VISIBILIDADE = '/v1/admin/visibilidade';

export const restDataSource: CompilacoesDataSource = {
  kind: 'rest',

  async me(signal) {
    return toMe(await request('/v1/me', { recurso: 'a sua sessão', signal }));
  },

  async resumo(signal) {
    return toResumo(await request('/v1/resumo', { recurso: 'o resumo das versões', signal }));
  },

  async listarBuilds(filtros, cursor, signal) {
    const resposta = await request('/v1/builds', {
      recurso: 'o feed de versões',
      signal,
      query: {
        produto: filtros.produto,
        ramo: filtros.ramo,
        branch: filtros.branch,
        status: filtros.status && filtros.status.length > 0 ? filtros.status : undefined,
        q: filtros.q?.trim() || undefined,
        publicado_de: filtros.publicado_de || undefined,
        publicado_ate: filtros.publicado_ate || undefined,
        com_incidente: filtros.com_incidente ? true : undefined,
        limit: filtros.limit,
        cursor,
      },
    });
    return toPagina(resposta);
  },

  async obterBuild(versao, produto = PRODUTO_PADRAO, signal) {
    return toBuildDetalhe(
      await request(caminhoVersao(versao), { recurso: `a versão ${versao}`, query: { produto }, signal }),
    );
  },

  async alterarStatus(versao, status, motivo, produto = PRODUTO_PADRAO) {
    return toBuildDetalhe(
      await request(`${caminhoVersao(versao)}/status`, {
        recurso: `o status da versão ${versao}`,
        method: 'POST',
        query: { produto },
        body: { status, motivo: motivo?.trim() || null },
      }),
    );
  },

  async listarRamos(produto, signal) {
    const resposta = await request<unknown>('/v1/ramos', { recurso: 'os ramos', query: { produto }, signal });
    return (Array.isArray(resposta) ? resposta : []).map(toRamo);
  },

  async obterPainel(signal) {
    return toPainel(await request('/v1/painel', { recurso: 'o painel do cliente', signal }));
  },

  async publicarPainel(versao) {
    return toPainel(
      await request('/v1/painel', { recurso: 'a publicação no painel do cliente', method: 'POST', body: { versao } }),
    );
  },

  async listarIncidentes(filtros, cursor, signal) {
    const resposta = await request('/v1/incidentes', {
      recurso: 'os incidentes',
      signal,
      query: {
        produto: filtros.produto,
        status: filtros.status && filtros.status.length > 0 ? filtros.status : undefined,
        severidade: filtros.severidade && filtros.severidade.length > 0 ? filtros.severidade : undefined,
        versao: filtros.versao?.trim() || undefined,
        q: filtros.q?.trim() || undefined,
        criado_de: filtros.criado_de || undefined,
        criado_ate: filtros.criado_ate || undefined,
        limit: filtros.limit,
        cursor,
      },
    });
    return toPaginaIncidentes(resposta);
  },

  async obterIncidente(id, signal) {
    return toIncidenteDetalhe(await request(caminhoIncidente(id), { recurso: `o incidente #${id}`, signal }));
  },

  async listarIncidentesDaVersao(versao, produto = PRODUTO_PADRAO, signal) {
    const resposta = await request<unknown>(`${caminhoVersao(versao)}/incidentes`, {
      recurso: `os incidentes da versão ${versao}`,
      query: { produto },
      signal,
    });
    return (Array.isArray(resposta) ? resposta : []).map(toIncidenteResumo);
  },

  async criarIncidente(dados) {
    return toIncidenteDetalhe(
      await request('/v1/incidentes', {
        recurso: 'o registro do incidente',
        method: 'POST',
        body: {
          titulo: dados.titulo.trim(),
          descricao: dados.descricao?.trim() || undefined,
          severidade: dados.severidade,
          link: dados.link?.trim() || undefined,
          corrigido_em: dados.corrigido_em?.trim() || undefined,
          versoes: dados.versoes,
          produto: dados.produto,
        },
      }),
    );
  },

  async editarIncidente(id, edicao) {
    return toIncidenteDetalhe(
      await request(caminhoIncidente(id), { recurso: `a edição do incidente #${id}`, method: 'PATCH', body: edicao }),
    );
  },

  async adicionarVersoesAoIncidente(id, versoes) {
    return toIncidenteDetalhe(
      await request(`${caminhoIncidente(id)}/versoes`, {
        recurso: `a associação de versões ao incidente #${id}`,
        method: 'POST',
        body: { versoes },
      }),
    );
  },

  async removerVersaoDoIncidente(id, versao) {
    return toIncidenteDetalhe(
      await request(`${caminhoIncidente(id)}/versoes/${encodeURIComponent(versao)}`, {
        recurso: `a remoção da versão ${versao} do incidente #${id}`,
        method: 'DELETE',
      }),
    );
  },

  async apagarIncidente(id) {
    await request(caminhoIncidente(id), { recurso: `a exclusão do incidente #${id}`, method: 'DELETE' });
  },

  async listarUsuariosComCapabilities(signal) {
    const resposta = await request<unknown>(`${ADMIN}`, { recurso: 'os usuários com capabilities', signal });
    return (Array.isArray(resposta) ? resposta : []).map(toUsuarioComCapabilities);
  },

  async buscarUsuariosKeycloak(query, signal) {
    const resposta = await request<unknown>(`${ADMIN}/buscar`, {
      recurso: 'a busca de usuários',
      query: { query: query.trim() },
      signal,
    });
    return (Array.isArray(resposta) ? resposta : []).map(toUsuarioKeycloak);
  },

  async obterCapabilitiesDoUsuario(keycloakUserId, signal) {
    return toCapabilitiesDoUsuario(
      await request(`${ADMIN}/${encodeURIComponent(keycloakUserId)}`, { recurso: 'as capabilities do usuário', signal }),
    );
  },

  async concederCapability(keycloakUserId, capability) {
    await request(`${ADMIN}/${encodeURIComponent(keycloakUserId)}`, {
      recurso: 'a concessão da capability',
      method: 'POST',
      body: { capability },
    });
  },

  async revogarCapability(keycloakUserId, capability) {
    await request(`${ADMIN}/${encodeURIComponent(keycloakUserId)}/${encodeURIComponent(capability)}`, {
      recurso: 'a revogação da capability',
      method: 'DELETE',
    });
  },

  async listarEventosCapabilities(limit = 50, signal) {
    const resposta = await request<unknown>(`${ADMIN}/eventos`, {
      recurso: 'a auditoria de capabilities',
      query: { limit },
      signal,
    });
    return (Array.isArray(resposta) ? resposta : []).map(toEventoCapability);
  },

  async listarVisibilidade(signal) {
    return toVisibilidade(await request(VISIBILIDADE, { recurso: 'a visibilidade', signal }));
  },

  async alterarVisibilidadeFuncionalidade(funcionalidade, audiencia) {
    return toVisibilidadeFuncionalidade(
      await request(`${VISIBILIDADE}/funcionalidades/${encodeURIComponent(funcionalidade)}`, {
        recurso: 'a visibilidade da funcionalidade',
        method: 'PUT',
        body: { audiencia },
      }),
    );
  },

  async alterarVisibilidadeRamo(produto, ramo, audiencia) {
    return toVisibilidadeRamo(
      await request(`${VISIBILIDADE}/ramos/${encodeURIComponent(produto)}/${encodeURIComponent(ramo)}`, {
        recurso: 'a visibilidade do ramo',
        method: 'PUT',
        body: { audiencia },
      }),
    );
  },

  async listarEventosVisibilidade(limit = 50, signal) {
    const resposta = await request<unknown>(`${VISIBILIDADE}/eventos`, {
      recurso: 'o histórico de visibilidade',
      query: { limit },
      signal,
    });
    return (Array.isArray(resposta) ? resposta : []).map(toEventoVisibilidade);
  },
};
